import { useState, useEffect } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function SolutionSection() {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: chartRef, isVisible: chartVisible } = useScrollAnimation({ threshold: 0.2 });

  // ROI Calculator State
  const [leadsPerMonth, setLeadsPerMonth] = useState(200);
  const [avgTicket, setAvgTicket] = useState(500);
  const [currentConversion, setCurrentConversion] = useState(5);

  // Animação das barras do gráfico
  const [animateBars, setAnimateBars] = useState(false);

  useEffect(() => {
    if (chartVisible) {
      const timer = setTimeout(() => setAnimateBars(true), 300);
      return () => clearTimeout(timer);
    }
  }, [chartVisible]);

  // Cálculos do ROI
  const currentRevenue = leadsPerMonth * (currentConversion / 100) * avgTicket;
  const improvedConversion = Math.min(currentConversion * 2.5, 35);
  const projectedRevenue = leadsPerMonth * (improvedConversion / 100) * avgTicket;
  const additionalRevenue = projectedRevenue - currentRevenue;
  const roiPercentage = Math.round((additionalRevenue / 997) * 100);

  const features = [
    'Responde clientes na hora',
    'Faz follow-up automático',
    'Organiza conversas',
    'Registra histórico completo',
    'Funciona 24/7 sem parar',
    'Opera via WhatsApp, Instagram e Messenger',
  ];

  // Dados do gráfico comparativo
  const comparisonData = [
    {
      metric: 'Tempo de Resposta',
      before: { value: 4, label: '4 horas', percentage: 80 },
      after: { value: 3, label: '3 seg', percentage: 6 },
      icon: 'ri-time-line',
      improvement: '99%',
    },
    {
      metric: 'Taxa de Conversão',
      before: { value: 5, label: '5%', percentage: 20 },
      after: { value: 12.5, label: '12.5%', percentage: 50 },
      icon: 'ri-line-chart-line',
      improvement: '+150%',
    },
    {
      metric: 'Leads Perdidos',
      before: { value: 40, label: '40%', percentage: 80 },
      after: { value: 5, label: '5%', percentage: 10 },
      icon: 'ri-user-unfollow-line',
      improvement: '-87%',
    },
    {
      metric: 'Atendimentos/Dia',
      before: { value: 50, label: '50', percentage: 25 },
      after: { value: 500, label: '500+', percentage: 100 },
      icon: 'ri-customer-service-2-line',
      improvement: '10x',
    },
    {
      metric: 'Satisfação',
      before: { value: 65, label: '65%', percentage: 65 },
      after: { value: 94, label: '94%', percentage: 94 },
      icon: 'ri-emotion-happy-line',
      improvement: '+45%',
    },
  ];

  const formatCurrency = (value: number) => {
    try {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    } catch (error) {
      console.error('Currency formatting error:', error);
      return value.toString();
    }
  };

  return (
    <section id="solucao" className="py-16 sm:py-24 bg-gradient-to-b from-dark to-navy-dark relative overflow-hidden">
      {/* Animated gradient orbs */}
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-primary/10 rounded-full blur-3xl"></div>

      <div ref={sectionRef} className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div
          className={`text-center mb-10 sm:mb-16 transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
            A Solução — Sistema Completo de <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent-cyan">IA</span>
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-4 sm:gap-8">
          <div
            className={`md:col-span-1 bg-dark-secondary rounded-xl sm:rounded-2xl p-5 sm:p-8 border border-dark-border transition-all duration-700 ease-out hover:border-secondary/50 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '100ms' }}
          >
            <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-transparent bg-clip-text bg-gradient-to-r from-secondary to-accent-purple">O Que É</h3>
            <div className="space-y-3 sm:space-y-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-2 sm:gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-lg sm:text-xl text-primary"></i>
                  </div>
                  <span className="text-gray-300 text-sm sm:text-base">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          <div
            className={`md:col-span-1 space-y-4 sm:space-y-6 transition-all duration-700 ease-out ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            <div className="bg-dark-secondary rounded-xl sm:rounded-2xl overflow-hidden border border-dark-border h-48 sm:h-64">
              <img
                src="https://public.readdy.ai/ai/img_res/dd2b409fa79ecef4a5453e9edf33081d.jpg"
                alt="Interface Conversacional"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="bg-gradient-to-r from-secondary to-accent-purple rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center border border-secondary/50">
              <div className="flex items-center justify-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-white/20 rounded-lg">
                  <i className="ri-brain-line text-xl sm:text-2xl text-white"></i>
                </div>
                <span className="text-sm sm:text-lg font-bold whitespace-nowrap text-white">IA baseada em ChatGPT</span>
              </div>
            </div>
          </div>

          <div
            className={`md:col-span-1 bg-dark-secondary rounded-xl sm:rounded-2xl p-5 sm:p-8 border border-primary/50 flex flex-col items-center justify-center text-center transition-all duration-700 ease-out hover:shadow-lg hover:shadow-primary/20 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '300ms' }}
          >
            <div className="text-5xl sm:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent-cyan mb-3 sm:mb-4 whitespace-nowrap">24/7</div>
            <p className="text-gray-300 text-sm sm:text-lg">
              Atendimento ininterrupto, todos os dias, sem pausas, sem feriados, sem descanso.
            </p>
          </div>
        </div>

        <div
          className={`mt-8 sm:mt-12 text-center transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '400ms' }}
        >
          <p className="text-base sm:text-xl italic text-accent-yellow font-medium px-4">
            Não é chatbot engessado. É IA ajustada ao seu negócio.
          </p>
        </div>

        {/* Gráfico Comparativo Antes vs Depois */}
        <div
          ref={chartRef}
          className={`mt-14 sm:mt-20 transition-all duration-700 ease-out ${
            chartVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="text-center mb-8 sm:mb-12">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
              <span className="text-red-400">Antes</span> vs <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent-cyan">Depois</span> da IA
            </h3>
            <p className="text-gray-400 text-sm sm:text-lg px-4">Veja a transformação real nos números do seu negócio</p>
          </div>

          <div className="bg-dark-secondary rounded-2xl sm:rounded-3xl border border-dark-border p-4 sm:p-6 md:p-10">
            {/* Legenda */}
            <div className="flex justify-center gap-6 sm:gap-8 mb-6 sm:mb-10">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-red-400/80"></div>
                <span className="text-gray-400 text-xs sm:text-sm">Sem IA</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-gradient-to-r from-primary to-accent-cyan"></div>
                <span className="text-gray-400 text-xs sm:text-sm">Com IA Neuro</span>
              </div>
            </div>

            {/* Barras Comparativas */}
            <div className="space-y-6 sm:space-y-8">
              {comparisonData.map((item, index) => (
                <div key={index} className="group" style={{ transitionDelay: `${index * 100}ms` }}>
                  {/* Métrica Header */}
                  <div className="flex items-center justify-between mb-2 sm:mb-3">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-dark-tertiary rounded-lg border border-dark-border group-hover:border-primary/50 transition-colors">
                        <i className={`${item.icon} text-lg sm:text-xl text-primary`}></i>
                      </div>
                      <span className="text-white font-semibold text-xs sm:text-base">{item.metric}</span>
                    </div>
                    <div className="bg-gradient-to-r from-primary/20 to-accent-cyan/20 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full flex-shrink-0 border border-primary/30">
                      <span className="text-primary font-bold text-xs sm:text-sm whitespace-nowrap">{item.improvement}</span>
                    </div>
                  </div>

                  {/* Barras */}
                  <div className="space-y-1.5 sm:space-y-2">
                    {/* Barra Antes */}
                    <div className="flex items-center gap-2 sm:gap-4">
                      <span className="text-gray-500 text-[10px] sm:text-xs w-10 sm:w-12 text-right flex-shrink-0">Antes</span>
                      <div className="flex-1 h-6 sm:h-8 bg-dark-tertiary rounded-lg overflow-hidden relative min-w-0">
                        <div
                          className="h-full bg-gradient-to-r from-red-500/80 to-red-400/60 rounded-lg transition-all duration-1000 ease-out flex items-center justify-end pr-2 sm:pr-3"
                          style={{
                            width: animateBars ? `${item.before.percentage}%` : '0%',
                            transitionDelay: `${index * 150}ms`,
                          }}
                        >
                          <span className="text-white text-[10px] sm:text-xs font-semibold whitespace-nowrap">
                            {item.before.label}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Barra Depois */}
                    <div className="flex items-center gap-2 sm:gap-4">
                      <span className="text-gray-500 text-[10px] sm:text-xs w-10 sm:w-12 text-right flex-shrink-0">Depois</span>
                      <div className="flex-1 h-6 sm:h-8 bg-dark-tertiary rounded-lg overflow-hidden relative min-w-0">
                        <div
                          className="h-full bg-gradient-to-r from-primary to-accent-cyan rounded-lg transition-all duration-1000 ease-out flex items-center justify-end pr-2 sm:pr-3 shadow-lg shadow-primary/20"
                          style={{
                            width: animateBars ? `${item.after.percentage}%` : '0%',
                            transitionDelay: `${index * 150 + 200}ms`,
                          }}
                        >
                          <span className="text-dark text-[10px] sm:text-xs font-semibold whitespace-nowrap">
                            {item.after.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Resumo Visual */}
            <div className="mt-8 sm:mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
              <div className="bg-dark-tertiary rounded-lg sm:rounded-xl p-3 sm:p-5 text-center border border-dark-border hover:border-primary/30 transition-colors">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mx-auto mb-2 sm:mb-3 bg-gradient-to-r from-primary/20 to-accent-cyan/20 rounded-full">
                  <i className="ri-rocket-2-line text-xl sm:text-2xl text-primary"></i>
                </div>
                <p className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent-cyan">10x</p>
                <p className="text-gray-500 text-[10px] sm:text-xs mt-1">Mais atendimentos</p>
              </div>
              <div className="bg-dark-tertiary rounded-lg sm:rounded-xl p-3 sm:p-5 text-center border border-dark-border hover:border-secondary/30 transition-colors">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mx-auto mb-2 sm:mb-3 bg-gradient-to-r from-secondary/20 to-accent-purple/20 rounded-full">
                  <i className="ri-timer-flash-line text-xl sm:text-2xl text-secondary-light"></i>
                </div>
                <p className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-secondary to-accent-purple">99%</p>
                <p className="text-gray-500 text-[10px] sm:text-xs mt-1">Mais rápido</p>
              </div>
              <div className="bg-dark-tertiary rounded-lg sm:rounded-xl p-3 sm:p-5 text-center border border-dark-border hover:border-primary/30 transition-colors">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mx-auto mb-2 sm:mb-3 bg-gradient-to-r from-primary/20 to-accent-cyan/20 rounded-full">
                  <i className="ri-money-dollar-circle-line text-xl sm:text-2xl text-primary"></i>
                </div>
                <p className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent-cyan">+150%</p>
                <p className="text-gray-500 text-[10px] sm:text-xs mt-1">Conversão</p>
              </div>
              <div className="bg-dark-tertiary rounded-lg sm:rounded-xl p-3 sm:p-5 text-center border border-dark-border hover:border-secondary/30 transition-colors">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mx-auto mb-2 sm:mb-3 bg-gradient-to-r from-secondary/20 to-accent-purple/20 rounded-full">
                  <i className="ri-heart-pulse-line text-xl sm:text-2xl text-secondary-light"></i>
                </div>
                <p className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-secondary to-accent-purple">94%</p>
                <p className="text-gray-500 text-[10px] sm:text-xs mt-1">Satisfação</p>
              </div>
            </div>
          </div>
        </div>

        {/* Calculadora de ROI */}
        <div
          className={`mt-14 sm:mt-20 transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '500ms' }}
        >
          <div className="text-center mb-6 sm:mb-10">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-accent-purple">Calculadora de ROI</span> — Veja Seu Potencial
            </h3>
            <p className="text-gray-400 text-sm sm:text-lg px-4">Descubra quanto você pode faturar a mais com nossa IA</p>
          </div>

          <div className="bg-dark-secondary rounded-2xl sm:rounded-3xl border border-dark-border p-4 sm:p-8 md:p-10">
            <div className="grid md:grid-cols-2 gap-6 sm:gap-10">
              {/* Inputs */}
              <div className="space-y-6 sm:space-y-8">
                {/* Leads por mês */}
                <div>
                  <div className="flex justify-between items-center mb-2 sm:mb-3 gap-2">
                    <label className="text-gray-300 font-medium flex items-center gap-2 text-sm sm:text-base">
                      <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                        <i className="ri-user-line text-primary"></i>
                      </div>
                      <span className="whitespace-nowrap">Leads/mês</span>
                    </label>
                    <span className="text-xl sm:text-2xl font-bold text-white">{leadsPerMonth}</span>
                  </div>
                  <input
                    type="range"
                    min="50"
                    max="1000"
                    step="10"
                    value={leadsPerMonth}
                    onChange={(e) => setLeadsPerMonth(Number(e.target.value))}
                    className="w-full h-2 bg-dark-tertiary rounded-full appearance-none cursor-pointer accent-primary [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-primary/30"
                  />
                  <div className="flex justify-between text-[10px] sm:text-xs text-gray-500 mt-1">
                    <span>50</span>
                    <span>1.000</span>
                  </div>
                </div>

                {/* Ticket médio */}
                <div>
                  <div className="flex justify-between items-center mb-2 sm:mb-3 gap-2">
                    <label className="text-gray-300 font-medium flex items-center gap-2 text-sm sm:text-base">
                      <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                        <i className="ri-money-dollar-circle-line text-secondary-light"></i>
                      </div>
                      <span className="whitespace-nowrap">Ticket médio</span>
                    </label>
                    <span className="text-xl sm:text-2xl font-bold text-white whitespace-nowrap">{formatCurrency(avgTicket)}</span>
                  </div>
                  <input
                    type="range"
                    min="100"
                    max="5000"
                    step="50"
                    value={avgTicket}
                    onChange={(e) => setAvgTicket(Number(e.target.value))}
                    className="w-full h-2 bg-dark-tertiary rounded-full appearance-none cursor-pointer accent-secondary [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-secondary [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-secondary/30"
                  />
                  <div className="flex justify-between text-[10px] sm:text-xs text-gray-500 mt-1">
                    <span>R$ 100</span>
                    <span>R$ 5.000</span>
                  </div>
                </div>

                {/* Taxa de conversão atual */}
                <div>
                  <div className="flex justify-between items-center mb-2 sm:mb-3 gap-2">
                    <label className="text-gray-300 font-medium flex items-center gap-2 text-sm sm:text-base">
                      <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                        <i className="ri-percent-line text-primary"></i>
                      </div>
                      <span className="whitespace-nowrap">Conversão atual</span>
                    </label>
                    <span className="text-xl sm:text-2xl font-bold text-white">{currentConversion}%</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="20"
                    step="1"
                    value={currentConversion}
                    onChange={(e) => setCurrentConversion(Number(e.target.value))}
                    className="w-full h-2 bg-dark-tertiary rounded-full appearance-none cursor-pointer accent-primary [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-primary/30"
                  />
                  <div className="flex justify-between text-[10px] sm:text-xs text-gray-500 mt-1">
                    <span>1%</span>
                    <span>20%</span>
                  </div>
                </div>
              </div>

              {/* Resultados */}
              <div className="bg-gradient-to-br from-dark-tertiary to-dark-secondary rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 border border-dark-border">
                <h4 className="text-base sm:text-lg font-semibold text-gray-400 mb-4 sm:mb-6 text-center">Projeção com IA</h4>

                <div className="space-y-4 sm:space-y-5">
                  {/* Faturamento atual */}
                  <div className="flex justify-between items-center pb-3 sm:pb-4 border-b border-dark-border gap-2">
                    <span className="text-gray-400 text-xs sm:text-base">Faturamento atual/mês</span>
                    <span className="text-base sm:text-xl font-bold text-gray-300 whitespace-nowrap">{formatCurrency(currentRevenue)}</span>
                  </div>

                  {/* Nova conversão */}
                  <div className="flex justify-between items-center pb-3 sm:pb-4 border-b border-dark-border gap-2">
                    <span className="text-gray-400 text-xs sm:text-base">Nova taxa de conversão</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs sm:text-sm text-gray-500 line-through">{currentConversion}%</span>
                      <span className="text-base sm:text-xl font-bold text-primary">{improvedConversion.toFixed(1)}%</span>
                    </div>
                  </div>

                  {/* Faturamento projetado */}
                  <div className="flex justify-between items-center pb-3 sm:pb-4 border-b border-dark-border gap-2">
                    <span className="text-gray-400 text-xs sm:text-base">Faturamento projetado/mês</span>
                    <span className="text-base sm:text-xl font-bold text-white whitespace-nowrap">{formatCurrency(projectedRevenue)}</span>
                  </div>

                  {/* Receita adicional */}
                  <div className="bg-gradient-to-r from-primary/10 to-accent-cyan/10 rounded-lg sm:rounded-xl p-3 sm:p-5 border border-primary/30">
                    <div className="text-center">
                      <p className="text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Receita adicional por mês</p>
                      <p className="text-xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent-cyan mb-1 sm:mb-2 break-words">
                        +{formatCurrency(additionalRevenue)}
                      </p>
                      <div className="flex items-center justify-center gap-1 sm:gap-2 text-accent-yellow">
                        <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                          <i className="ri-arrow-up-line"></i>
                        </div>
                        <span className="font-bold text-xs sm:text-base">ROI de {roiPercentage}%</span>
                      </div>
                    </div>
                  </div>

                  {/* Receita anual */}
                  <div className="text-center pt-2">
                    <p className="text-xs sm:text-sm text-gray-500">Potencial adicional em 12 meses</p>
                    <p className="text-lg sm:text-2xl font-bold text-white break-words">{formatCurrency(additionalRevenue * 12)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-6 sm:mt-8 text-center">
              <a
                href="https://wa.me/5541992584018?text=Olá! Quero automatizar meu atendimento com IA"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-primary to-accent-cyan hover:opacity-90 text-dark font-bold py-3 sm:py-4 px-6 sm:px-8 rounded-full transition-all duration-300 hover:scale-105 shadow-lg shadow-primary/30 cursor-pointer whitespace-nowrap text-sm sm:text-base"
              >
                <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-lg sm:text-xl"></i>
                </div>
                QUERO ESSE RESULTADO
              </a>
              <p className="text-gray-500 text-xs sm:text-sm mt-3 sm:mt-4">*Resultados baseados em dados reais de clientes</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
